import { router, useLocalSearchParams } from "expo-router";
import { useCallback, useMemo } from "react";

/**
 * Tiny hook for admin list pages: read + write a fixed schema of search /
 * filter params via the URL. Keeps state in the URL so deep-links and the
 * back button work, and so different admins can share filtered views.
 *
 * Usage:
 *   const filter = useAdminListFilter({
 *     keys: ["q", "role", "sort"],
 *     defaults: { q: "", role: "all", sort: "recent" },
 *   });
 *   filter.get("q");          // current value (string)
 *   filter.set({ role: "judge" });  // patch one or many
 *   filter.reset();           // back to defaults
 *
 * Implementation notes:
 *  - Each key is single-valued. Arrays in URLs are not supported here —
 *    use comma-separated strings if you need multi-select and split on read.
 *  - Empty string === default; we leave the param off the URL so the URL
 *    stays clean.
 */
export type AdminListFilterSchema<K extends string = string> = {
  keys: readonly K[];
  defaults: Partial<Record<K, string>>;
};

export type AdminListFilter<K extends string = string> = {
  get: (key: K) => string;
  set: (patch: Partial<Record<K, string>>) => void;
  reset: () => void;
  /** Current canonical values keyed by name. */
  values: Record<K, string>;
};

function firstString(v: string | string[] | undefined): string | undefined {
  if (v == null) return undefined;
  return Array.isArray(v) ? v[0] : v;
}

export function useAdminListFilter<K extends string>(
  schema: AdminListFilterSchema<K>
): AdminListFilter<K> {
  const params = useLocalSearchParams() as Record<string, string | string[] | undefined>;

  const values = useMemo(() => {
    const out = {} as Record<K, string>;
    for (const k of schema.keys) {
      const raw = firstString(params[k] as string | string[] | undefined);
      const def = schema.defaults[k] ?? "";
      out[k] = raw == null || raw === "" ? def : raw;
    }
    return out;
  }, [params, schema]);

  const get = useCallback(
    (key: K) => values[key] ?? schema.defaults[key] ?? "",
    [values, schema]
  );

  const set = useCallback(
    (patch: Partial<Record<K, string>>) => {
      const next: Record<string, string | undefined> = {};
      for (const k of schema.keys) {
        const incoming = patch[k];
        const current = values[k];
        const v = incoming != null ? incoming : current;
        const def = schema.defaults[k] ?? "";
        next[k] = v === def || v === "" ? undefined : v;
      }
      router.setParams(next as Record<string, string | string[] | undefined>);
    },
    [values, schema]
  );

  const reset = useCallback(() => {
    const cleared: Record<string, undefined> = {};
    for (const k of schema.keys) cleared[k] = undefined;
    router.setParams(cleared as Record<string, string | string[] | undefined>);
  }, [schema]);

  return { get, set, reset, values };
}
